import { ChevronDown, Languages } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { cn } from "@/lib/utils";

/**
 * Languages offered for voice transcription. Values are Whisper language
 * codes (Scaleway whisper-large-v3). Whisper supports the Nordic languages,
 * including Nynorsk ("nn") as a distinct code from Bokmål ("no").
 *
 * No "Auto" option on purpose: Whisper's auto-detect mangles Nordic audio
 * (Norwegian → Chinese), so we always send an explicit language hint.
 */
export const TRANSCRIPTION_LANGUAGE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "no", label: "Norsk (bokmål)" },
  { value: "nn", label: "Norsk (nynorsk)" },
  { value: "sv", label: "Svenska" },
  { value: "da", label: "Dansk" },
  { value: "fi", label: "Suomi" },
  { value: "is", label: "Íslenska" },
  { value: "en", label: "English" },
  { value: "de", label: "Deutsch" },
  { value: "es", label: "Español" },
  { value: "fr", label: "Français" },
  { value: "it", label: "Italiano" },
  { value: "nl", label: "Nederlands" },
  { value: "pt", label: "Português" },
  { value: "hi", label: "हिन्दी" },
  { value: "ja", label: "日本語" },
  { value: "zh", label: "中文" },
];

/** Fallback when the app locale doesn't map to a Whisper code (primary market is Nordic). */
const DEFAULT_LANGUAGE = "no";

/** A supported transcription language: `code` if offered, else the Nordic default. */
export function resolveTranscriptionLanguage(code: string | undefined): string {
  const initial = code ?? DEFAULT_LANGUAGE;
  return TRANSCRIPTION_LANGUAGE_OPTIONS.some((o) => o.value === initial)
    ? initial
    : DEFAULT_LANGUAGE;
}

type TranscriptionLanguageSelectProps = {
  value: string;
  onChange: (value: string) => void;
  /** Locked while a recording is being captured or transcribed. */
  disabled?: boolean;
  /** Accessible name for the trigger. */
  label: string;
  /** "sm" = 24px compact (editor toolbars), "md" = 32px with a chevron (chat composer). */
  size?: "sm" | "md";
};

/** Language hint picker for the voice transcriber. */
export function TranscriptionLanguageSelect({
  value,
  onChange,
  disabled,
  label,
  size = "sm",
}: TranscriptionLanguageSelectProps) {
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger
        className={cn(
          "w-auto font-medium [&>svg:last-child]:hidden",
          size === "md"
            ? "h-8 gap-1.5 border-0 bg-transparent px-2 text-xs text-muted-foreground shadow-none hover:bg-accent hover:text-foreground"
            : "h-6 gap-1 px-1.5 text-[10px]",
        )}
        aria-label={label}
      >
        <Languages className="h-3.5 w-3.5 shrink-0" />
        <span className="font-medium uppercase">{value}</span>
        {size === "md" && <ChevronDown className="h-3 w-3 shrink-0 opacity-70" />}
      </SelectTrigger>
      <SelectContent className="z-[100]">
        {TRANSCRIPTION_LANGUAGE_OPTIONS.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
